import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import {
  sortButtonModsQuery,
  fetchSortSources,
  removeReferenceFromSortLive,
  changeSortCorpusToggler,
  changeSortWorkflowToggler,
  updateButtonSort,
  closeSortUpdateAlert,
  setSortUpdating
} from '../actions/sortActions';
import { setReferenceCurie, setGetReferenceCurieFlag, getCuratorSourceId } from '../actions/biblioActions';
import { Spinner, Form, Container, Row, Col, Button, Alert, InputGroup } from 'react-bootstrap';
import "react-bootstrap-typeahead/css/Typeahead.css";
import { api } from "../api";
import Modal from 'react-bootstrap/Modal';
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import ReferencesToSort from './ReferencesToSort';
import HelpDocLink, { HELP_DOC_URLS } from './HelpDocLink';
import PropTypes from 'prop-types';

const RowDivider = () => { return (<Row><Col>&nbsp;</Col></Row>); }

// Label mapping for sort source values (module-level constant)
const sortSourceLabels = {
  'mod_pubmed_search': 'MOD PubMed Search',
  'dqm_files': 'DQM Files',
  'manual_creation': 'Manual Creation',
  'automated_alliance': 'Automated Alliance',
  'assigned_for_review': 'Assigned for Review',
  'prepublication_pipeline': 'Prepublication Pipeline',
  'gaf': 'GAF',
  'interaction': 'Interaction'
};

// Extracted component to avoid duplication of bulk selection controls
const BulkSelectionControls = ({
  checkboxId,
  referencesCount,
  isAllSelected,
  handleSelectAll,
  selectedCount,
  handleBulkMoveOut,
  bulkUpdating,
  clearSelection,
  activeMod
}) => {
  if (activeMod !== 'SGD') return null;

  return (
    <>
      {/* Empty checkbox-width column for alignment with paper cards */}
      <Col lg="auto" style={{ minWidth: '40px', padding: '.5rem' }}></Col>
      <Col lg={6} className="d-flex align-items-center">
        <Form.Check
          type="checkbox"
          id={checkboxId}
          label={`Select All (${referencesCount})`}
          checked={isAllSelected}
          onChange={handleSelectAll}
          style={{ fontWeight: 'bold', marginRight: '10px' }}
        />
        <Button
          variant="danger"
          size="sm"
          onClick={handleBulkMoveOut}
          disabled={selectedCount === 0 || bulkUpdating}
          style={{ marginRight: '5px' }}
        >
          {bulkUpdating ? (
            <>
              <Spinner animation="border" size="sm" /> Moving...
            </>
          ) : (
            `Move Selected OUT (${selectedCount})`
          )}
        </Button>
        {selectedCount > 0 && (
          <Button
            variant="link"
            size="sm"
            onClick={clearSelection}
          >
            Clear
          </Button>
        )}
      </Col>
      <Col lg={1}></Col>
    </>
  );
};

BulkSelectionControls.propTypes = {
  checkboxId: PropTypes.string.isRequired,
  referencesCount: PropTypes.number.isRequired,
  isAllSelected: PropTypes.bool.isRequired,
  handleSelectAll: PropTypes.func.isRequired,
  selectedCount: PropTypes.number.isRequired,
  handleBulkMoveOut: PropTypes.func.isRequired,
  bulkUpdating: PropTypes.bool.isRequired,
  clearSelection: PropTypes.func.isRequired,
  activeMod: PropTypes.string.isRequired
};

const Sort = () => {
  // Selectors
  const referencesToSortLive = useSelector(state => state.sort.referencesToSortLive);
  const accessToken = useSelector(state => state.isLogged.accessToken);
  const sortType = useSelector(state => state.sort.sortType);
  const sortUpdating = useSelector(state => state.sort.sortUpdating);
  const getPapersToSortFlag = useSelector(state => state.sort.getPapersToSortFlag);
  const isLoading = useSelector(state => state.sort.isLoading);
  const cognitoMod = useSelector(state => state.isLogged.cognitoMod);
  const testerMod = useSelector(state => state.isLogged.testerMod);
  const userEmail = useSelector(state => state.isLogged.email);  // use email instead of uid
  const userId = useSelector(state => state.isLogged.userId);

  const dispatch = useDispatch();

  const [speciesSelectLoading, setSpeciesSelectLoading] = useState([]);
  const speciesTypeaheadRef = useRef(null);
  const [speciesSelect, setSpeciesSelect] = useState([]);
  const [typeaheadOptions, setTypeaheadOptions] = useState([]);
  const [topicEntitySourceId, setTopicEntitySourceId] = useState(undefined);

  const [viewMode, setViewMode] = useState('Sort'); // 'Sort', 'Prepublication', or 'Recently sorted'
  const [selectedCurator, setSelectedCurator] = useState(userEmail || '');
  const [selectedTimeframe, setSelectedTimeframe] = useState('1');
  const [curatorOptions, setCuratorOptions] = useState([]);
  const [recentlySortedData, setRecentlySortedData] = useState([]);
  const [showInsidePapers, setShowInsidePapers] = useState(true);

  // Search and filter state
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortSource, setSortSource] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const sortSources = useSelector(state => state.sort.sortSources);
  const totalCount = useSelector(state => state.sort.totalCount);

  // Bulk selection state
  const [selectedPapers, setSelectedPapers] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  let accessLevel = testerMod !== 'No' ? testerMod : cognitoMod;
  let activeMod = accessLevel;

  // Fetch topic entity source id
  useEffect(() => {
    const fetchSourceId = async () => {
      if (accessToken !== null) {
        const sourceId = await getCuratorSourceId(accessLevel, accessToken);
        setTopicEntitySourceId(sourceId);
      }
    }
    fetchSourceId().catch(console.error);
  }, [accessLevel, accessToken]);

  let buttonUpdateDisabled = sortUpdating > 0;

  // Fetch available sort sources when entering Sort view
  useEffect(() => {
    if (viewMode === 'Sort' && accessLevel) {
      dispatch(fetchSortSources(accessLevel));
    }
  }, [viewMode, accessLevel, dispatch]);

  // Fetch "to sort" / prepublication list when viewMode changes
  useEffect(() => {
    let mappedSortType = null;
    if (viewMode === 'Sort') {
      mappedSortType = 'needs_review';
    } else if (viewMode === 'Prepublication') {
      mappedSortType = 'prepublication_pipeline';
    } else {
      return;
    }

    if (mappedSortType && sortUpdating === 0 && accessLevel) {
      if (mappedSortType === 'needs_review') {
        dispatch(sortButtonModsQuery(accessLevel, mappedSortType, searchQuery, sortSource, 'date_published', sortOrder));
      } else {
        dispatch(sortButtonModsQuery(accessLevel, mappedSortType));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Note: searchQuery, sortSource, sortOrder intentionally omitted - changes handled by explicit handlers
  }, [viewMode, sortUpdating, accessLevel, dispatch]);

  // Fetch recently sorted papers when viewMode is "Recently sorted"
  useEffect(() => {
    if (viewMode === 'Recently sorted' && accessToken && accessLevel) {
      fetchRecentlySortedPapers(accessLevel, selectedTimeframe, selectedCurator);
      setShowInsidePapers(true);
    }
  }, [viewMode, accessToken, accessLevel, selectedTimeframe, selectedCurator]);

  const fetchRecentlySortedPapers = (modAbbreviation, day, curatorEmail) => {
    const url = `/sort/recently_sorted?mod_abbreviation=${modAbbreviation}&day=${day}&curator=${curatorEmail}`;

    api.get(url)
      .then(response => response.data)
      .then(data => {
        const { curator_data, data: references } = data;

        // curator_data is now a mapping: name -> email
        let curators = Object.entries(curator_data).map(([name, email]) => ({ name, email }));

        const loggedInUserEmail = userEmail;
        const loggedInUserIncluded = curators.some(curator => curator.email === loggedInUserEmail);

        // Move logged-in user to the top if present
        if (loggedInUserIncluded) {
          const index = curators.findIndex(curator => curator.email === loggedInUserEmail);
          if (index > 0) {
            const [loggedInUser] = curators.splice(index, 1);
            curators.unshift(loggedInUser);
          }
        }

        setCuratorOptions(curators);
        setRecentlySortedData(references);

        // Ensure selectedCurator is valid (still in the list)
        if (!curators.some(curator => curator.email === selectedCurator)) {
          if (curators.length > 0) {
            setSelectedCurator(curators[0].email);
          } else {
            setSelectedCurator('');
          }
        }
      })
      .catch(error => {
        console.error('Error fetching recently sorted papers:', error);
      });
  };

  // Search handler
  const handleSearch = () => {
    setSearchQuery(searchInputValue);
    setSelectedPapers(new Set());
    dispatch(sortButtonModsQuery(accessLevel, 'needs_review', searchInputValue, sortSource, 'date_published', sortOrder));
  };

  // Sort source filter handler
  const handleSortSourceChange = (newSortSource) => {
    setSortSource(newSortSource);
    setSelectedPapers(new Set());
    dispatch(sortButtonModsQuery(accessLevel, 'needs_review', searchQuery, newSortSource, 'date_published', sortOrder));
  };

  // Sort order handler
  const handleSortOrderChange = (newSortOrder) => {
    setSortOrder(newSortOrder);
    setSelectedPapers(new Set());
    dispatch(sortButtonModsQuery(accessLevel, 'needs_review', searchQuery, sortSource, 'date_published', newSortOrder));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchInputValue('');
    setSearchQuery('');
    setSortSource('');
    setSortOrder('desc');
    setSelectedPapers(new Set());
    dispatch(sortButtonModsQuery(accessLevel, 'needs_review', '', '', 'date_published', 'desc'));
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(referencesToSortLive.map(ref => ref.mod_corpus_association_id));
      setSelectedPapers(allIds);
    } else {
      setSelectedPapers(new Set());
    }
  };

  const handleSelectPaper = (mcaId) => {
    setSelectedPapers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(mcaId)) {
        newSet.delete(mcaId);
      } else {
        newSet.add(mcaId);
      }
      return newSet;
    });
  };

  const isAllSelected = referencesToSortLive && referencesToSortLive.length > 0 &&
    referencesToSortLive.every(ref => selectedPapers.has(ref.mod_corpus_association_id));

  // Bulk move OUT handler
  const handleBulkMoveOut = async () => {
    if (selectedPapers.size === 0) return;

    setBulkUpdating(true);
    setBulkResult(null);

    try {
      const response = await api.patch('/reference/mod_corpus_association/batch', {
        mod_corpus_association_ids: Array.from(selectedPapers),
        corpus: false,
        force_out: false
      });

      const result = response.data;
      setBulkResult(result);

      // Remove successfully updated papers from the list
      const successfulIds = new Set(
        result.results.filter(r => r.success).map(r => r.mod_corpus_association_id)
      );

      // Clear selection and trigger refresh
      setSelectedPapers(new Set());

      if (successfulIds.size > 0) {
        // Refresh the list to remove sorted papers
        dispatch(sortButtonModsQuery(accessLevel, 'needs_review', searchQuery, sortSource, 'date_published', sortOrder));
      }
    } catch (error) {
      console.error('Bulk update error:', error);
      setBulkResult({
        total_requested: selectedPapers.size,
        successful: 0,
        failed: selectedPapers.size,
        results: [{ success: false, message: error.response?.data?.detail || 'Bulk update failed' }]
      });
    } finally {
      setBulkUpdating(false);
    }
  };

  // Handler for 'Find sorted papers' button
  const handleFindSortedPapers = () => {
    let curator = selectedCurator;
    console.log(`Selected Curator Before Check: ${curator}`);
    if (curator === null || curator === undefined || curator === '') {
      curator = userEmail;
      console.log("Reset curator to logged in user email:", curator);
      setSelectedCurator(userEmail || '');
    }
    console.log(`Fetching papers with curator: ${curator}`);
    fetchRecentlySortedPapers(accessLevel, selectedTimeframe, curator);
  }

  // Function to update sorting
  function updateSorting() {
    const forApiArray = []
    for (const [index, reference] of referencesToSortLive.entries()) {
      if (reference['mod_corpus_association_corpus'] !== null) {
        let updateJson = { 'corpus': reference['mod_corpus_association_corpus'], 'mod_corpus_sort_source': 'manual_creation' }
        let subPath = `reference/mod_corpus_association/${reference['mod_corpus_association_id']}`;
        const field = null;
        const subField = null;
        let method = 'PATCH';
        let array = [accessToken, subPath, updateJson, method, index, field, subField]
        forApiArray.push(array);
        if (reference['mod_corpus_association_corpus'] === true && activeMod === "WB") {
          let reference_type = null;
          if (reference['workflow'] === 'experimental') { reference_type = 'Experimental'; }
          else if (reference['workflow'] === 'not_experimental') { reference_type = 'Not_experimental'; }
          else if (reference['workflow'] === 'meeting') { reference_type = 'Meeting_abstract'; }
          if (reference_type !== null) {
            updateJson = { 'reference_type': reference_type, 'mod_abbreviation': activeMod, 'reference_curie': reference['curie'] }
            subPath = 'reference/mod_reference_type/';
            method = 'POST';
            array = [accessToken, subPath, updateJson, method, index, field, subField]
            forApiArray.push(array);
            if (speciesSelect && speciesSelect[index]) {
              for (const item of speciesSelect[index].values()) {
                const taxArray = item.split(" ");
                updateJson = {
                  'reference_curie': reference['curie'],
                  'entity': taxArray.pop(),
                  'topic': "ATP:0000123",
                  'entity_type': "ATP:0000123",
                  'entity_id_validation': "alliance",
                  'tag_source_id': topicEntitySourceId,
                  'data_novelty': "ATP:0000335",
                  'negated': false
                };
                subPath = 'topic_entity_tag/';
                const field = null;
                const subField = null;
                method = 'POST';
                array = [accessToken, subPath, updateJson, method, index, field, subField]
                forApiArray.push(array);
              }
            }
          }
        }
      }
    }

    setSpeciesSelect([]);
    setTypeaheadOptions([]);
    setSpeciesSelectLoading([]);
    if (speciesTypeaheadRef.current) {
      speciesTypeaheadRef.current.clear();
    }

    let dispatchCount = forApiArray.length;

    console.log('dispatchCount ' + dispatchCount)
    dispatch(setSortUpdating(dispatchCount))

    for (const arrayData of forApiArray) {
      dispatch(updateButtonSort(arrayData))
    }
  }

  return (
    <div>
      <h3>References for {accessLevel} <HelpDocLink url={HELP_DOC_URLS.modCorpusSorting} title="Open the MOD corpus sorting help documentation" /></h3>
      <Form>
        <Form.Check
          inline
          type='radio'
          name='viewMode'
          id='viewModeSort'
        >
          <Form.Check.Input
            type='radio'
            name='viewMode'
            id='viewModeSort'
            checked={viewMode === 'Sort'}
            onChange={() => setViewMode('Sort')}
          />
          <Form.Check.Label style={{ fontSize: '1.1em' }}>Sort</Form.Check.Label>
        </Form.Check>

        {/* Conditionally render 'Prepublication' radio button if activeMod is 'WB' */}
        {activeMod === 'WB' && (
          <Form.Check
            inline
            type='radio'
            name='viewMode'
            id='viewModePrepublication'
          >
            <Form.Check.Input
              type='radio'
              name='viewMode'
              id='viewModePrepublication'
              checked={viewMode === 'Prepublication'}
              onChange={() => setViewMode('Prepublication')}
            />
            <Form.Check.Label style={{ fontSize: '1.1em' }}>Prepublication</Form.Check.Label>
          </Form.Check>
        )}

        <Form.Check
          inline
          type='radio'
          name='viewMode'
          id='viewModeRecentlySorted'
        >
          <Form.Check.Input
            type='radio'
            name='viewMode'
            id='viewModeRecentlySorted'
            checked={viewMode === 'Recently sorted'}
            onChange={() => setViewMode('Recently sorted')}
          />
          <Form.Check.Label style={{ fontSize: '1.1em' }}>Recently sorted</Form.Check.Label>
        </Form.Check>
      </Form>
      <Container fluid>
        {viewMode === 'Sort' && isLoading && (
          <div
            className="sort-loading-indicator"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <Spinner animation="border" size="sm">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <span>Refreshing papers...</span>
          </div>
        )}
        {(viewMode === 'Sort' || viewMode === 'Prepublication') &&
          <>
            {/* Search and Filter Controls - Only for Sort view */}
            {viewMode === 'Sort' && (
              <>
                <RowDivider />
                <Row className="mb-3 align-items-end justify-content-center">
                  <Col md={4}>
                    <Form.Group className="mb-0">
                      <Form.Label style={{ fontWeight: 'bold' }}>Search:</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          placeholder="Search titles, abstracts, journals, authors..."
                          value={searchInputValue}
                          onChange={(e) => setSearchInputValue(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <Button variant="outline-secondary" onClick={handleSearch}>
                          Search
                        </Button>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md="auto">
                    <Form.Group className="mb-0">
                      <Form.Label style={{ fontWeight: 'bold' }}>Source:</Form.Label>
                      <Form.Control
                        as="select"
                        value={sortSource}
                        onChange={(e) => handleSortSourceChange(e.target.value)}
                        style={{ minWidth: '180px' }}
                      >
                        <option value="">All Sources</option>
                        {sortSources.map(source => (
                          <option key={source} value={source}>
                            {sortSourceLabels[source] || source}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md="auto">
                    <Form.Group className="mb-0">
                      <Form.Label style={{ fontWeight: 'bold' }}>Order:</Form.Label>
                      <Form.Control
                        as="select"
                        value={sortOrder}
                        onChange={(e) => handleSortOrderChange(e.target.value)}
                        style={{ minWidth: '130px' }}
                      >
                        <option value="desc">Newest First</option>
                        <option value="asc">Oldest First</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md="auto">
                    {(searchQuery || sortSource || sortOrder !== 'desc') && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={handleClearFilters}
                        title="Clear all filters"
                      >
                        Clear
                      </Button>
                    )}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <small className="text-muted">
                      {viewMode === 'Sort' && (
                        <span>
                          <strong>{totalCount}</strong> total papers
                          {referencesToSortLive && referencesToSortLive.length > 0 && totalCount > referencesToSortLive.length && (
                            <span> (showing {referencesToSortLive.length})</span>
                          )}
                        </span>
                      )}
                      {(searchQuery || sortSource) && <span> | </span>}
                      {searchQuery && <span>Searching for: "<strong>{searchQuery}</strong>"</span>}
                      {searchQuery && sortSource && <span> | </span>}
                      {sortSource && <span>Source: <strong>{sortSourceLabels[sortSource] || sortSource}</strong></span>}
                    </small>
                  </Col>
                </Row>
                {/* Bulk Result Alert - only for SGD */}
                {activeMod === 'SGD' && bulkResult && (
                  <Row className="mb-3">
                    <Col>
                      <Alert
                        variant={bulkResult.failed === 0 ? 'success' : bulkResult.successful === 0 ? 'danger' : 'warning'}
                        onClose={() => setBulkResult(null)}
                        dismissible
                      >
                        <Alert.Heading>
                          {bulkResult.failed === 0 ? 'Bulk Update Successful' : 'Bulk Update Complete'}
                        </Alert.Heading>
                        <p>
                          <strong>{bulkResult.successful}</strong> papers moved OUT successfully.
                          {bulkResult.failed > 0 && (
                            <span> <strong>{bulkResult.failed}</strong> failed.</span>
                          )}
                        </p>
                        {bulkResult.failed > 0 && (
                          <details>
                            <summary>Show failed items</summary>
                            <ul style={{ maxHeight: '150px', overflowY: 'auto' }}>
                              {bulkResult.results
                                .filter(r => !r.success)
                                .map((r, idx) => (
                                  <li key={idx}>
                                    {r.reference_curie || `ID: ${r.mod_corpus_association_id}`}: {r.message}
                                  </li>
                                ))}
                            </ul>
                          </details>
                        )}
                      </Alert>
                    </Col>
                  </Row>
                )}
              </>
            )}
            <RowDivider />
            {referencesToSortLive && referencesToSortLive.length > 0 &&
              <Row className="align-items-center justify-content-center">
                <BulkSelectionControls
                  checkboxId="select-all-papers"
                  referencesCount={referencesToSortLive.length}
                  isAllSelected={isAllSelected}
                  handleSelectAll={handleSelectAll}
                  selectedCount={selectedPapers.size}
                  handleBulkMoveOut={handleBulkMoveOut}
                  bulkUpdating={bulkUpdating}
                  clearSelection={() => setSelectedPapers(new Set())}
                  activeMod={activeMod}
                />
                <Col lg={2} className="text-center">
                  <SortSubmitUpdateRouter />
                  <Button
                    as="input"
                    style={{ width: '200px' }}
                    type="button"
                    disabled={buttonUpdateDisabled}
                    value="Update Sorting"
                    onClick={() => updateSorting()}
                  />
                </Col>
                <Col lg={2}></Col>
              </Row>
            }
            {referencesToSortLive && referencesToSortLive.length === 0 && (
              <div>
                <br />
                {(searchQuery || sortSource) ? (
                  <p>No papers found matching your search criteria. <Button variant="link" onClick={handleClearFilters}>Clear filters</Button></p>
                ) : (
                  <p>No Papers to sort</p>
                )}
              </div>
            )}
            {referencesToSortLive && referencesToSortLive.length > 0 && (
              <Container fluid>
                <RowDivider />
                {referencesToSortLive.map((reference, index) => (
                  <ReferencesToSort
                    key={`reference-${reference.mod_corpus_association_id}`}
                    reference={reference}
                    index={index}
                    canSort={true}
                    speciesSelect={speciesSelect}
                    setSpeciesSelect={setSpeciesSelect}
                    speciesSelectLoading={speciesSelectLoading}
                    setSpeciesSelectLoading={setSpeciesSelectLoading}
                    typeaheadOptions={typeaheadOptions}
                    setTypeaheadOptions={setTypeaheadOptions}
                    speciesTypeaheadRef={speciesTypeaheadRef}
                    topicEntitySourceId={topicEntitySourceId}
                    accessToken={accessToken}
                    activeMod={activeMod}
                    isSelected={selectedPapers.has(reference.mod_corpus_association_id)}
                    onSelectChange={handleSelectPaper}
                    showCheckbox={viewMode === 'Sort' && activeMod === 'SGD'}
                  />
                ))}
                <RowDivider />
                <Row className="align-items-center justify-content-center">
                  <BulkSelectionControls
                    checkboxId="select-all-papers-bottom"
                    referencesCount={referencesToSortLive.length}
                    isAllSelected={isAllSelected}
                    handleSelectAll={handleSelectAll}
                    selectedCount={selectedPapers.size}
                    handleBulkMoveOut={handleBulkMoveOut}
                    bulkUpdating={bulkUpdating}
                    clearSelection={() => setSelectedPapers(new Set())}
                    activeMod={activeMod}
                  />
                  <Col lg={2} className="text-center">
                    <SortSubmitUpdateRouter />
                    <Button
                      as="input"
                      style={{ width: '200px' }}
                      type="button"
                      disabled={buttonUpdateDisabled}
                      value="Update Sorting"
                      onClick={() => updateSorting()}
                    />
                  </Col>
                  <Col lg={2}></Col>
                </Row>
              </Container>
            )}
          </>
        }

        {viewMode === 'Recently sorted' &&
          <>
            <Row className="justify-content-center">
              <Col lg="auto">
                <br />
                <Form>
                  <Form.Row className="align-items-end">
                    <Col md={5}>
                      <Form.Group controlId="formCuratorSelect">
                        <Form.Label style={{ fontWeight: 'bold' }}>Who:</Form.Label>
                        <Form.Control
                          as="select"
                          style={{ minWidth: '250px' }}
                          value={selectedCurator}
                          onChange={(e) => setSelectedCurator(e.target.value)}
                        >
                          {curatorOptions.map((curator, index) => (
                            <option key={index} value={curator.email}>
                              {curator.name}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group controlId="formTimeframeSelect">
                        <Form.Label style={{ fontWeight: 'bold' }}>When:</Form.Label>
                        <Form.Control
                          as="select"
                          value={selectedTimeframe}
                          onChange={(e) => setSelectedTimeframe(e.target.value)}
                        >
                          <option value="1">Today</option>
                          <option value="7">Past week</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                      <Form.Group>
                        <Form.Label>&nbsp;</Form.Label> {/* Empty label for alignment */}
                        <Button onClick={handleFindSortedPapers}>Find sorted papers</Button>
                      </Form.Group>
                    </Col>
                  </Form.Row>
                </Form>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <br />
                {recentlySortedData && recentlySortedData.length > 0 ? (
                  <div>
                    <SortSubmitUpdateRouter />
                    <Button
                      variant="outline-primary"
                      className="ml-1"
                      onClick={() => setShowInsidePapers(!showInsidePapers)}
                    >
                      {showInsidePapers ? 'Show Outside Papers' : 'Show Inside Papers'}
                    </Button>
                    <p />
                    <Container fluid>
                      {recentlySortedData
                        .filter(ref => ref['mod_corpus_association_corpus'] === showInsidePapers)
                        .map((reference, index) => (
                          <ReferencesToSort
                            key={`reference-${reference.mod_corpus_association_id}`}
                            reference={reference}
                            index={index}
                            canSort={true}
                            speciesSelect={speciesSelect}
                            setSpeciesSelect={setSpeciesSelect}
                            speciesSelectLoading={speciesSelectLoading}
                            setSpeciesSelectLoading={setSpeciesSelectLoading}
                            typeaheadOptions={typeaheadOptions}
                            setTypeaheadOptions={setTypeaheadOptions}
                            speciesTypeaheadRef={speciesTypeaheadRef}
                            topicEntitySourceId={topicEntitySourceId}
                            accessToken={accessToken}
                            activeMod={activeMod}
                          />
                        ))}
                      {recentlySortedData.filter(ref => ref['mod_corpus_association_corpus'] === showInsidePapers).length === 0 && (
                        <p>No {showInsidePapers ? 'inside' : 'outside'} papers found.</p>
                      )}
                    </Container>
                  </div>
                ) : (
                  <p>No sorted papers found.</p>
                )}
              </Col>
            </Row>
          </>
        }
      </Container>
      <hr />
    </div>
  )
}

// SortSubmitUpdateRouter Component
const SortSubmitUpdateRouter = () => {
  const sortUpdating = useSelector(state => state.sort.sortUpdating);

  if (sortUpdating > 0) {
    return (<SortSubmitUpdating />);
  }
  else {
    return (<><AlertDismissibleSortUpdate /></>);
  }
}

// SortSubmitUpdating Component
const SortSubmitUpdating = () => {
  return (
    <Alert
      variant="secondary"
      className="sort-status-alert"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <Alert.Heading>Updating Sort data...</Alert.Heading>
    </Alert>
  );
}

// AlertDismissibleSortUpdate Component
const AlertDismissibleSortUpdate = () => {
  const dispatch = useDispatch();
  const updateAlert = useSelector(state => state.sort.updateAlert);
  const updateFailure = useSelector(state => state.sort.updateFailure);
  const updateMessages = useSelector(state => state.sort.updateMessages);
  const accessToken = useSelector(state => state.isLogged.accessToken);

  const [sortModalText, setSortModalText] = useState("")
  const [showTetModal, setShowTetModal] = useState(false);
  const [tetErrorMessage, setTetErrorMessage] = useState("");
  const [modCorpusAssociationId, setModCorpusAssociationId] = useState(null);

  useEffect(() => {
    if (sortModalText.includes("Curated topic and entity tags")) {
      const idMatch = sortModalText.match(/mod_corpus_association_id\s*=\s*(\d+)/);
      if (idMatch) {
        setModCorpusAssociationId(idMatch[1]);
      }
      const displayMessage = sortModalText.replace(/mod_corpus_association_id\s*=\s*\d+/, "");
      setTetErrorMessage(displayMessage);
      setShowTetModal(true);
      setSortModalText('');
    }
  }, [sortModalText, dispatch]);

  useEffect(() => {
    if (updateAlert && updateFailure > 0) {
      const curatedTagsMessage = updateMessages.find(msg =>
        msg.includes("Curated topic and entity tags")
      );
      if (curatedTagsMessage) {
        const idMatch = curatedTagsMessage.match(/mod_corpus_association_id\s*=\s*(\d+)/);
        if (idMatch) {
          setModCorpusAssociationId(idMatch[1]);
        }
        setTetErrorMessage(
          "Curated topic and entity tags or automated tags generated from your MOD " +
          "are associated with this reference. Please check with the curator who added these tags."
        );
        setShowTetModal(true);
        dispatch(closeSortUpdateAlert());
      }
    }
  }, [updateAlert, updateMessages, dispatch]);

  const handleDeleteTetTagsAndPaper = async () => {
    if (!modCorpusAssociationId) {
      return;
    }
    const url = `/reference/mod_corpus_association/${modCorpusAssociationId}`;
    try {
      await api.patch(url, { 'corpus': false, 'force_out': true });
      setShowTetModal(false);
      setModCorpusAssociationId(null);
      window.location.reload();
    } catch (err) {
      console.error("Failed to delete workflow / TET tags and remove the paper from mod corpus:", err);
      const errorDetail = err.response?.data?.detail || err.response?.data?.message || "Deletion failed";
      setSortModalText(errorDetail);
    }
  };

  const handleCloseTetModal = () => {
    setShowTetModal(false);
    setModCorpusAssociationId(null);
    setTetErrorMessage("");
  };

  const variant = updateFailure === 0 ? 'success' : 'danger';
  const header = updateFailure === 0 ? 'Update Success' : 'Update Failure';

  return (
    <>
      {/* Curated Tags Modal */}
      <Modal show={showTetModal} onHide={handleCloseTetModal}>
        <Modal.Header closeButton>
          <Modal.Title>Curated Topic/Entity Tags Found</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{tetErrorMessage}</p>
          {modCorpusAssociationId && (
            <p className="text-muted small">Association ID: {modCorpusAssociationId}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseTetModal}>
            Keep Tags and Paper
          </Button>
          <Button variant="danger" onClick={handleDeleteTetTagsAndPaper}>
            Delete Tags and Paper
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Regular Alert (for non-curated-tag messages) */}
      {updateAlert && !showTetModal ? (
        <Alert
          variant={variant}
          className="sort-status-alert"
          onClose={() => dispatch(closeSortUpdateAlert())}
          dismissible
        >
          <Alert.Heading>{header}</Alert.Heading>
          {updateMessages.map((message, index) => (
            <div key={`${message} ${index}`}>{message}</div>
          ))}
        </Alert>
      ) : null}

    </>
  );
}

export default Sort;
